// The meta profile (phase E1, decision 0060: achievements "saved and synced with progress"): the achievements, the
// codex and the retired tutorial prompts as one @rpg/meta profile document of versioned slices, kept in the save store
// under its own key beside the settings and synced with the save's cloud sync under the "merge" policy (@rpg/save):
// both devices' unlocks are kept, never asked about. No browser imports (the Node tools use it too).
import { fromJsonBytes, jsonBytes } from "@rpg/save";
import {
  emptyAchievements,
  emptyCodex,
  emptyTutorials,
  makeProfile,
  mergeAchievements,
  mergeCodex,
  mergeProfiles,
  mergeTutorials,
  readProfile,
  type AchievementState,
  type CodexState,
  type MetaProfile,
  type ProfileSliceDef,
  type TutorialState,
  type Tutorials,
} from "@rpg/meta";
import type { MetaLayer } from "./module";

/** The save store's key for the profile. */
export const META_PROFILE_KEY = "meta";

/** The profile's slices, their versions and merges. */
export const PROFILE_SLICES: Record<string, ProfileSliceDef<unknown>> = {
  achievements: { version: 1, merge: (a, b) => mergeAchievements(a as AchievementState, b as AchievementState), empty: emptyAchievements },
  codex: { version: 1, merge: (a, b) => mergeCodex(a as CodexState, b as CodexState), empty: emptyCodex },
  tutorials: { version: 1, merge: (a, b) => mergeTutorials(a as TutorialState, b as TutorialState), empty: emptyTutorials },
};

/** The profile now (the layer's achievements and codex; the page's tutorials, when there are any). */
export function profileOf(layer: MetaLayer, tutorials: Tutorials | null): MetaProfile {
  return makeProfile(PROFILE_SLICES, { achievements: layer.achievements.snapshot(), codex: layer.codex.snapshot(), tutorials: tutorials?.snapshot() ?? emptyTutorials() });
}

/** Merge a stored profile into the layer and the tutorials (never taking progress back). Returns the achievements it
 *  unlocked here and the codex entries it found. Throws on a document that isn't a profile. */
export function applyProfile(layer: MetaLayer, tutorials: Tutorials | null, raw: unknown): { unlocked: string[]; found: string[] } {
  const p = readProfile(PROFILE_SLICES, raw);
  const unlocked = layer.achievements.merge(p.achievements as AchievementState);
  const found = layer.codex.merge(p.codex as CodexState);
  tutorials?.merge(p.tutorials as TutorialState);
  return { unlocked, found };
}

/** The sync's merge for the profile's key: two stored profiles' bytes in, the merged profile's bytes out. */
export function mergeProfileBytes(a: Uint8Array, b: Uint8Array): Uint8Array {
  return jsonBytes(mergeProfiles(PROFILE_SLICES, fromJsonBytes(a), fromJsonBytes(b)));
}
